import type { Locale } from '@whizz/react-branding/dist/@types/types';
import type { GroupStudentLoginSheets, StudentLoginSheet } from '@whizz/api-model/dist/@types/endpoints/GroupStudentLoginSheets';
import type { OptionTypeBase } from 'react-select';
import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { AccessCode, Button, ButtonIcon, ChoiceGroup, Loader, Table } from '../../components';
import { Mascot, MathsWhizzLogo, WhizzEducationLogo, palette } from '@whizz/react-branding';
import styled, { createGlobalStyle, css } from 'styled-components';
import { usePortal } from '../../hooks/usePortal';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../util/vars';
import { model } from '@whizz/api-client';
import { useQuery } from 'react-query';
import exportFromJson from 'export-from-json';
import { Headline } from '../../global';
import { StudentPin } from '../../components/studentPin/StudentPin';
import { getIconComponent } from '../../util/reactFunc';

const TABLE_ID = 'student-logins';

const GlobalStyle = createGlobalStyle`
  #${TABLE_ID} {
    margin-top: 0.5rem;
    th {
      display: none;
    }
    tr {
      display: table;
      width: 100%;
    }
    td {
      display: table-row;
      width: 100%;
      height: auto;
      font-weight: unset;

      & > * {
        display: table-cell;
        vertical-align: middle;
        padding: 0.5rem;
      }
      ::before {
        display: table-cell;
        vertical-align: middle;
        padding: 0.5rem;
        font-weight: bold;
        content: attr(data-label) ":";
      }
    }

    [data-id^='col_student'] > div,
    [data-id^='col_normalLogin'] > div,
    [data-id^='col_classPictureLogin'] > div {
      justify-content: center;
      flex-direction: column;
      gap: 0.25rem;
      font-style: normal;
      font-weight: bold;
    }

    @media (max-width: 639px) {
      th[data-id="col_pin"] {
        display: flex;
        padding: 0;
        font-style: normal;
        height: auto;
        margin-bottom: 1rem;
        font-size: 1rem;
      }
    }

    @media (min-width: 640px) {
      margin-top: 0;
      th {
        display: table-cell;
      }
      tr {
        display: table-row;
      }
      td {
        display: table-cell;
        width: 16.65%;
        height: 45px;

        ::before {
          display: none;
        }

         & > * {
          display: contents;
        }
      }

      [data-id='col_name'],
      [data-column='cell_name'],

      [data-id='col_username'],
      [data-column='cell_username'],

      [data-id='col_pin'],
      [data-column='cell_pin'] {
        box-shadow: -4px 0 4px -2px rgba(0, 0, 0, 0.2);
      }

      [data-id='col_name'],
      [data-column='cell_name'],
      [data-id='col_pin'],
      [data-column='cell_pin'] {
        width: 33.3%;
      }

      [data-id='col_pin'] > div,
      [data-column='cell_pin'] > div > div {
        justify-content: center;
      }
    }
  }
`;

const StudentLogins = ({ classValue, accessCode }: { classValue: OptionTypeBase | null, accessCode: string | null }): JSX.Element => {
  const { translate, locale } = useI18n();

  const selectedClassId = classValue?.value;
  const schoolClassName = classValue?.label ?? '';

  const studentsQuery = useQuery(
    [ QUERIES_IDS.CLASSES, selectedClassId ],
    () => model.StudentLoginSheets.get(selectedClassId),
    {
      enabled: null != selectedClassId,
    },
  );

  const students = studentsQuery.data?.loginSheets?.sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  }) ?? [];
  const [ selectedStudents, setSelectedStudents ] = useState<StudentLoginSheet['username'][]>([]);
  const [ showPasswords, setShowPasswords ] = useState<StudentLoginSheet['username'][]>([]);
  const [ showPins, setShowPins ] = useState<StudentLoginSheet['username'][]>([]);

  const onClickSelectAll = () => {
    setSelectedStudents((state) => {
      if (state.length === students.length) {
        return [];
      }
      return students.map((students) => students.username);
    });
  };
  const onClickStudent = (username: StudentLoginSheet['username']) => () => {
    setSelectedStudents((state) => {
      if (state.includes(username)) {
        return state.filter((n) => n !== username);
      }
      return [ ...state, username ];
    });
  };
  const onClickPassword = (username: StudentLoginSheet['username']) => () => {
    setShowPasswords((state) => {
      if (state.includes(username)) {
        return state.filter((n) => n !== username);
      }
      return [ ...state, username ];
    });
  };
  const onClickPin = (username: StudentLoginSheet['username']) => () => {
    setShowPins((state) => {
      if (state.includes(username)) {
        return state.filter((n) => n !== username);
      }
      return [ ...state, username ];
    });
  };

  const columns = [
    {
      Header: '',
      id: 'student',
      columns: [
        {
          id: 'name',
          Header: <ChoiceGroup id={'select_all'} label={translate('pages.HOME.MESSAGE_STUDENTS.SELECT_ALL')} checked={selectedStudents.length > 0 && (selectedStudents.length === students.length)} onChange={onClickSelectAll} />,
          accessor: (row: StudentLoginSheet) => `${row.firstName} ${row.lastName}`,
          disableSortBy: true,
          Cell: ({ row }: { row: any }) => <ChoiceGroup id={row.original.username} label={`${row.original.firstName} ${row.original.lastName}`} checked={selectedStudents.includes(row.original.username)} onChange={onClickStudent(row.original.username)} />,
        },
      ],
    },
    {
      Header: (<>{translate('pages.STUDENT_LOGINS.NORMAL_LOGIN')} <NormalLoginIcon /></>),
      id: 'normalLogin',
      columns: [
        {
          id: 'username',
          Header: translate('pages.STUDENT_LOGINS.TABLE.USERNAME'),
          accessor: 'username',
        },
        {
          id: 'password',
          Header: translate('pages.STUDENT_LOGINS.TABLE.PASSWORD'),
          accessor: 'password',
          disableSortBy: true,
          Cell: ({ row }: { row: any }) => <Password password={row.values.password} show={showPasswords.includes(row.original.username)} onClick={onClickPassword(row.original.username)} />,
        },
      ],
    },
    {
      Header: (<>{translate('pages.STUDENT_LOGINS.CLASS_PICTURE_LOGIN')} <StudentPin pin={'ADI'} size={'sm'} show /></>),
      id: 'classPictureLogin',
      columns: [
        {
          id: 'pin',
          Header: (
            <div className={'flex items-center justify-center gap-2'}>
              <h3 className={'sm:hidden'}>{translate('pages.STUDENT_LOGINS.CLASS_PICTURE_LOGIN')}</h3>
              <h3 className={'hidden sm:block'}>{translate('pages.STUDENT_LOGINS.TABLE.PIN')}</h3>
              <AccessCode code={accessCode} />
            </div>),
          title: translate('pages.STUDENT_LOGINS.LOGIN_CARD.PIN'),
          accessor: 'pin',
          disableSortBy: true,
          Cell: ({ row }: { row: any }) => <Password key={`pw-${row.original.username}`} onClick={onClickPin(row.original.username)} show={showPins.includes(row.original.username)}><StudentPin key={`pin-${row.original.username}`} pin={row.values.pin} show={showPins.includes(row.original.username)} /></Password>,
        },
      ],
    },
  ];

  const handleDownloadLogins = () => {
    if (students.length > 0) {
      const fields: Record<keyof Omit<StudentLoginSheet, 'pin'>, string> = {
        firstName: translate('pages.STUDENT_LOGINS.TABLE.FIRST_NAME'),
        lastName: translate('pages.STUDENT_LOGINS.TABLE.LAST_NAME'),
        username: translate('pages.STUDENT_LOGINS.TABLE.USERNAME'),
        password: translate('pages.STUDENT_LOGINS.TABLE.PASSWORD'),
      };
      exportFromJson({ data: students, fileName: translate('pages.STUDENT_LOGINS.FILENAME', { class: schoolClassName }), exportType: 'csv', withBOM: true, fields: fields });
    }
  };

  const handlePrintLogins = () => {
    window.print();
  };

  const loginsToPrint = students.filter((student) => selectedStudents.includes(student.username));
  const loginSheet = usePortal(<LoginSheet students={loginsToPrint} schoolClassName={schoolClassName} locale={locale?.id as Locale ?? 'en-gb'} />);
  const isLoading =
    (null != selectedClassId
      && (
        !studentsQuery.isFetched
        || studentsQuery.isFetching
      ));

  const content = () => {
    if (isLoading) {
      return <Loader />;
    }
    if (!students.length) {
      return (
        <>
          <Headline title={translate('pages.HOME.GET_STARTED.NO_STUDENTS')} />
          <div className={'text-muted italic mt-5 space-y-2'}>
            <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info1')}</p>
            <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info2')}</p>
          </div>
        </>
      );
    }
    return (
      <>
        <GlobalStyle />
        <div>
          <div className='hidden sm:flex items-center gap-4 w-max' data-cy={'buttons'}>
            <Button isOutline color={'header'} onClick={handleDownloadLogins}>{translate('pages.STUDENT_LOGINS.BUTTONS.DOWNLOAD')}</Button>
            <Button isOutline color={'header'} onClick={handlePrintLogins} disabled={selectedStudents.length === 0}>{translate('pages.STUDENT_LOGINS.BUTTONS.PRINT')}</Button>
          </div>
          <Table
            id={TABLE_ID}
            cols={columns}
            content={students}
            footerPosition={null}
          />
        </div>
        {loginSheet}
      </>
    );
  };

  useEffect(() => {
    const root = document.getElementById('root');
    root?.classList.add('hide-on-print');
    return () => {
      root?.classList.remove('hide-on-print');
    };
  }, []);

  return (
    <>
      {content()}
    </>
  );
};

const NormalLoginIcon = () => {
  const { translate } = useI18n();
  return (
    <div className={'flex flex-col items-center'}>
      <div className={'flex items-center'} style={{ 'marginBottom': 2 }}>
        {getIconComponent('Student3d', 16, 'other')}
        <div className={'bg-white border-2 border-gray-300 rounded-md w-10'} style={{ fontSize: 8, lineHeight: '8px', padding: '1px 3px' }}>
          <div className={'capitalize'}>{translate('pages.STUDENT_LOGINS.LOGIN')}</div>
        </div>
      </div>
      <div className={'flex items-center'}>
        {getIconComponent('Lock', 16, 'other')}
        <div className={'bg-white border-2 border-gray-300 rounded-md w-10'} style={{ fontSize: 12, lineHeight: '12px', padding: '1px 3px' }}>
          <div style={{ height: 8 }}>****</div>
        </div>
      </div>
    </div>
  );
};

const LoginSheet = ({ students, schoolClassName, locale }: { students: StudentLoginSheet[], schoolClassName: GroupStudentLoginSheets['className'], locale: Locale }) => {
  return (
    <LoginSheetStyled $locale={locale}>
      {students.map((student) => <LoginCard key={student.username} student={student} schoolClassName={schoolClassName} />)}
    </LoginSheetStyled>
  );
};

const LoginCard = ({ student, schoolClassName }: { student: StudentLoginSheet, schoolClassName: GroupStudentLoginSheets['className'] }) => {
  const { translate, locale } = useI18n();
  const { firstName, lastName, username, password, pin } = student;
  return (
    <div className={'inline-block w-1/2 h-1/4 p-2 text-sm break-inside-avoid'}>
      <div className='border-2 p-2' style={{ borderColor: palette.green.main }}>
        <div className='border-2 p-2 space-y-2' style={{ borderColor: palette.blue.main }}>
          <div className={'flex justify-end h-8'}>
            <MathsWhizzLogo tutoringPlusByline={false} locale={locale?.getId() as Locale} />
          </div>
          <div className='flex justify-center'>
            <DetailsTable>
              <tbody>
                <tr>
                  <td className={'font-bold'}>{translate('pages.STUDENT_LOGINS.LOGIN_CARD.NAME')}</td>
                  <td className={'inline-block w-48 overflow-hidden whitespace-nowrap text-ellipsis'}>{`${firstName} ${lastName}`}</td>
                </tr>
                <tr>
                  <td className={'font-bold'}>{translate('pages.STUDENT_LOGINS.LOGIN_CARD.CLASS')}</td>
                  <td className={'inline-block w-48 overflow-hidden whitespace-nowrap text-ellipsis'}>{schoolClassName}</td>
                </tr>
                <tr>
                  <td className={'font-bold'}>{translate('pages.STUDENT_LOGINS.LOGIN_CARD.USERNAME')}</td>
                  <td>{username}</td>
                </tr>
                <tr>
                  <td className={'font-bold'}>{translate('pages.STUDENT_LOGINS.LOGIN_CARD.PASSWORD')}</td>
                  <td>{password}</td>
                </tr>
                <tr>
                  <td className={'font-bold'}>{translate('pages.STUDENT_LOGINS.LOGIN_CARD.PIN')}</td>
                  <td><StudentPin pin={pin} size={'lg'} /></td>
                </tr>
              </tbody>
            </DetailsTable>
          </div>
          <div className={'grid grid-cols-3 items-center'}>
            <WhizzEducationLogo solid className={'h-12'} />
            <h2 className={'justify-self-center'}>whizz.com</h2>
            <Mascot className={'h-12 transform -rotate-6 justify-self-end'} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Password = ({ password, show = false, onClick, children }: { password?: string, show?: boolean, onClick?: React.MouseEventHandler, children?: React.ReactNode }) => {
  return (
    <div className={'flex items-center'}>
      <div>{children ?? (show ? password : '∗∗∗∗∗')}</div>
      {onClick ? <div><ButtonIcon icon={GLOBAL_ICONS[show ? 'see_off' : 'see_on']} size={'sm'} isCircle color={'transparent'} onClick={onClick} /></div> : null}
    </div>
  );
};

const DetailsTable = styled.table`
  td {
    padding: 0.2rem;
  }
`;

const LoginSheetStyledBase = styled.div`
  display: none;
  height: auto;
  flex-wrap: wrap;
  background: #FFF;
  @media print {
    display: block;
  }
`;

const LoginSheetStyled = styled(LoginSheetStyledBase) <{ $locale: Locale }>`
  ${({ $locale }) => {
    switch ($locale) {
      case 'en-us':
        return css`
          @page {
            size: letter portrait;
          }
        `;
      default:
        return css`
          @page {
            size: A4 portrait;
          }
        `;
    }
  }}
`;

export default StudentLogins;
